import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import LoadingContainer from 'components/LoadingContainer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { DocumentUploadLeftArrow } from '../../assets/svg/sendDocuments'
import { SendPrescription } from '../../new-components/SendDocument'
import { RedLabelInformation, StepsSendDocuments } from '../../new-components/atoms'
import { ModalConfirm } from '../../new-components/molecules/ModalConfirm'
import { TModalConfirmMethods } from '../../new-components/molecules/ModalConfirm/types'

interface SendPrescripitonDocUIProps {
  documentId: string | null
  handleContinue: any
  prescriptionFiles: any
  pillPrescriptionFiles: any
  orderData: any
  orderLoading: any
  existingPrescription: any
  existingPillPrescription: any
  deletePrescription: any
  handlePrescriptionDrop: any
  handlePrescriptionFileSelect: any
  handlePillPrescriptionDrop: any
  handlePillPrescriptionFileSelect: any
  handleTakePhoto: any
  handleTakePhotoPill: any
  handleDragOver: any
  handleDragLeave: any
  setCurrentStep: any
  receitaSuccess: any
  pillPrescriptionSuccess: any
  sendDocuments: any
  prescriptionUploadState: any
  pillPrescriptionUploadState: any
  deleteDocument: any
  documentRefused: any
  setDocumentRefused: any
  hasUploaded: any
  setHasUploaded: any
  responseType: 'normal' | 'ocr'
  automaticReadingEnabled: boolean
  setAutomaticReadingEnabled: (value: boolean) => void
  onKeepDocument: () => void
  isConfirmModalOpen: boolean
  setIsConfirmModalOpen: (value: boolean) => void
  missingBrandsList: any[]
  proceedFromModal: () => Promise<void>
  /** Associação exige laudo médico independente do tipo de produto */
  alwaysRequireLaudo?: boolean
}

const PILL_SECTION_COLOR = '#2B7CE0'

const PillIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={PILL_SECTION_COLOR}
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </svg>
)

export const SendPrescripitonDocUI = ({
  documentId,
  handleContinue,
  prescriptionFiles,
  pillPrescriptionFiles,
  orderData,
  orderLoading,
  existingPrescription,
  existingPillPrescription,
  deletePrescription,
  handlePrescriptionDrop,
  handlePrescriptionFileSelect,
  handlePillPrescriptionDrop,
  handlePillPrescriptionFileSelect,
  handleTakePhoto,
  handleTakePhotoPill,
  handleDragOver,
  handleDragLeave,
  setCurrentStep: setHookCurrentStep,
  receitaSuccess,
  pillPrescriptionSuccess,
  sendDocuments,
  prescriptionUploadState,
  pillPrescriptionUploadState,
  deleteDocument,
  documentRefused,
  setDocumentRefused,
  hasUploaded,
  setHasUploaded,
  responseType,
  automaticReadingEnabled,
  setAutomaticReadingEnabled,
  onKeepDocument,
  isConfirmModalOpen,
  setIsConfirmModalOpen,
  missingBrandsList,
  proceedFromModal,
  alwaysRequireLaudo = false
}: SendPrescripitonDocUIProps) => {
  const handleSuccess = useCallback(() => {
    toast.success('Receita enviada com sucesso!')
  }, [])
  const [currentStep, setCurrentStep] = useState(1)
  const modalConfirmRef = useRef<TModalConfirmMethods>(null)

  useEffect(() => {
    if (isConfirmModalOpen && modalConfirmRef.current) {
      modalConfirmRef.current.onShow()
    } else if (!isConfirmModalOpen && modalConfirmRef.current) {
      modalConfirmRef.current.onClose()
    }
  }, [isConfirmModalOpen])

  useEffect(() => {
    setHookCurrentStep('prescription')
  }, [])

  if (orderLoading) {
    return <LoadingContainer loading={orderLoading} />
  }

  if (!orderData) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '20px 0' }}>
        <div style={{ height: '100vh', backgroundColor: '#f5f5f5', padding: '20px 0' }}>
          <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontSize: '1.1rem' }}>
            Pedido não encontrado. Redirecionando...
          </div>
        </div>
      </div>
    )
  }

  const items = orderData?.order?.items.filter((item: any) => item.product?.type === 'PRODUTO') || []

  const controlledItems = items.filter((item: any) => item.requires_controlled_prescription)
  const requiresControlledPrescription = controlledItems.length > 0

  const isPillOnly: boolean = orderData?.order?.summary?.is_pill_only ?? orderData?.order?.is_pill_only ?? false

  const allProductsPharmacy = items.every((item: any) => item.supplier_id === 18)
  const hasLaudoProducts =
    alwaysRequireLaudo ||
    items.some((item: any) => item.product?.delivery_by_association === true || item.class_id === 4)

  const allProductsAreSupplements = items.every((item: any) => item.class_id === 6)

  const allProductsAreFreightBrand = items.every((item: any) => BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id))

  const allProductsDontNeedAnvisa = items.every(
    (item: any) =>
      item.product?.delivery_by_association === true ||
      item.class_id === 4 ||
      item.class_id === 6 ||
      BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id)
  )

  let customSteps

  if (alwaysRequireLaudo && !allProductsDontNeedAnvisa) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Autorização Anvisa' },
      { number: 5, text: 'Dados da entrega' }
    ]
  } else if (alwaysRequireLaudo) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Dados da entrega' }
    ]
  } else if (allProductsPharmacy) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Dados da entrega' }
    ]
  } else if (allProductsAreSupplements) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Dados da entrega' }
    ]
  } else if (allProductsAreFreightBrand) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Dados da entrega' }
    ]
  } else if (allProductsDontNeedAnvisa && hasLaudoProducts) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Dados da entrega' }
    ]
  } else if (hasLaudoProducts && !allProductsDontNeedAnvisa) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Autorização Anvisa' },
      { number: 5, text: 'Dados da entrega' }
    ]
  } else {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Autorização Anvisa' },
      { number: 4, text: 'Dados da entrega' }
    ]
  }

  return (
    <div style={{ minHeight: '100vh', paddingTop: '20px' }}>
      {!isPillOnly && (
        <div style={{ paddingBottom: '20px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <StepsSendDocuments
              currentStep={currentStep}
              automaticReadingEnabled={automaticReadingEnabled}
              onToggleAutomaticReading={setAutomaticReadingEnabled}
              customSteps={customSteps}
            />
          </div>
        </div>
      )}

      <div style={{ padding: '20px 0' }}>
        <div style={{ width: '100%', padding: '0 20px', maxWidth: '1200px', margin: '0 auto' }}>
          <RedLabelInformation className="text-xs">
            {isPillOnly
              ? 'Para seguirmos com o seu pedido, é necessário anexar a receita de controle especial válida.'
              : 'Para seguirmos com a importação, é necessário anexar a prescrição médica válida.'}
          </RedLabelInformation>
        </div>
      </div>

      <div style={{ padding: '0 0 64px 0' }}>
        <div style={{ width: '100%', padding: '0 20px', maxWidth: '1200px', margin: '0 auto' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '14px',
              padding: '30px 34px',
              boxShadow: '0 1px 3px rgba(30,40,30,.05)'
            }}>
            {isPillOnly ? (
              requiresControlledPrescription && (
                <SendPrescription
                  className="rounded-none bg-transparent p-0"
                  onDrop={handlePillPrescriptionDrop}
                  onFileSelect={handlePillPrescriptionFileSelect}
                  onTakePhoto={handleTakePhotoPill}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  supportedFiles={['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']}
                  maxSize="10MB"
                  title="Envie a receita do medicamento controlado"
                  titleColor={PILL_SECTION_COLOR}
                  icon={<PillIcon />}
                  description={`Seu pedido contém medicamento controlado que exige uma receita específica: ${controlledItems
                    .map((item: any) => item.name)
                    .filter(Boolean)
                    .join(', ')}. Envie a receita de controle especial para este(s) produto(s).`}
                  yellowLabelInformation
                  descriptionInformation="A receita de controle especial precisa estar dentro da validade e assinada por um profissional com CRM/CRO válido."
                  stepName="pill_prescription"
                  requestFile={pillPrescriptionFiles[0]}
                  responseType="normal"
                  onSuccess={handleSuccess}
                  documentId={documentId || ''}
                  existingDocuments={existingPillPrescription}
                  onDeleteDocument={deletePrescription}
                  sendDocuments={sendDocuments}
                  uploadState={pillPrescriptionUploadState}
                  deleteDocument={deleteDocument}
                  documentRefused={false}
                  setDocumentRefused={setDocumentRefused}
                  hasUploaded={hasUploaded}
                  setHasUploaded={setHasUploaded}
                  onKeepDocument={onKeepDocument}
                />
              )
            ) : (
              <>
                <SendPrescription
                  className="rounded-none bg-transparent p-0"
                  onDrop={handlePrescriptionDrop}
                  onFileSelect={handlePrescriptionFileSelect}
                  onTakePhoto={handleTakePhoto}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  supportedFiles={['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']}
                  maxSize="10MB"
                  title="Envie sua receita médica"
                  icon={<DocumentUploadLeftArrow />}
                  description="Estamos quase lá! Envie sua receita para que nosso sistema valide o documento e finalize a importação com segurança."
                  yellowLabelInformation
                  descriptionInformation="A receita médica precisa ter sido emitida nos últimos 5 meses e assinada por um profissional com CRM/CRO válido."
                  stepName="prescription"
                  requestFile={prescriptionFiles[0]}
                  responseType={responseType}
                  onSuccess={handleSuccess}
                  documentId={documentId || ''}
                  existingDocuments={existingPrescription}
                  onDeleteDocument={deletePrescription}
                  sendDocuments={sendDocuments}
                  uploadState={prescriptionUploadState}
                  deleteDocument={deleteDocument}
                  documentRefused={documentRefused}
                  setDocumentRefused={setDocumentRefused}
                  hasUploaded={hasUploaded}
                  setHasUploaded={setHasUploaded}
                  onKeepDocument={onKeepDocument}
                />

                {requiresControlledPrescription && (
                  <>
                    <div style={{ height: '1px', background: '#ECEAE4', margin: '28px 0' }} />
                    <SendPrescription
                      className="rounded-none bg-transparent p-0"
                      onDrop={handlePillPrescriptionDrop}
                      onFileSelect={handlePillPrescriptionFileSelect}
                      onTakePhoto={handleTakePhotoPill}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      multiple
                      supportedFiles={['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']}
                      maxSize="10MB"
                      title="Envie a receita do medicamento controlado"
                      titleColor={PILL_SECTION_COLOR}
                      icon={<PillIcon />}
                      description={`Seu pedido contém medicamento controlado que exige uma receita específica: ${controlledItems
                        .map((item: any) => item.name)
                        .filter(Boolean)
                        .join(', ')}. Envie a receita de controle especial para este(s) produto(s).`}
                      yellowLabelInformation
                      descriptionInformation="A receita de controle especial precisa estar dentro da validade e assinada por um profissional com CRM/CRO válido."
                      stepName="pill_prescription"
                      requestFile={pillPrescriptionFiles[0]}
                      responseType="normal"
                      onSuccess={handleSuccess}
                      documentId={documentId || ''}
                      existingDocuments={existingPillPrescription}
                      onDeleteDocument={deletePrescription}
                      sendDocuments={sendDocuments}
                      uploadState={pillPrescriptionUploadState}
                      deleteDocument={deleteDocument}
                      documentRefused={false}
                      setDocumentRefused={setDocumentRefused}
                      hasUploaded={hasUploaded}
                      setHasUploaded={setHasUploaded}
                      onKeepDocument={onKeepDocument}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {(receitaSuccess || (isPillOnly && pillPrescriptionSuccess)) && (
        <div style={{ padding: '40px 0', textAlign: 'end' }}>
          <div style={{ width: '100%', padding: '0 20px' }}>
            <button
              onClick={handleContinue}
              disabled={
                isPillOnly
                  ? requiresControlledPrescription && existingPillPrescription.length === 0
                  : existingPrescription.length === 0 ||
                    (requiresControlledPrescription && existingPillPrescription.length === 0)
              }
              style={{
                background: '#748351',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '16px 48px',
                fontSize: '1.1rem',
                fontWeight: 600,
                transition: 'background-color 0.2s',
                minWidth: '200px'
              }}>
              Continuar
            </button>
          </div>
        </div>
      )}

      {!isPillOnly && (
        <ModalConfirm
          ref={modalConfirmRef}
          title="Marcas não identificadas"
          text={
            <div>
              <p>Identificamos que as seguintes marcas do seu pedido não foram encontradas nas receitas enviadas:</p>
              <ul style={{ listStyleType: 'none', paddingLeft: '0', marginTop: '10px', marginBottom: '10px' }}>
                {missingBrandsList.map((b: any, index: number) => (
                  <li key={index} style={{ marginBottom: '10px' }}>
                    <strong>{b.name}</strong>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '5px' }}>
                      {b.products.map((p: string, pIndex: number) => (
                        <li key={pIndex}>{p}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <p>Deseja prosseguir mesmo assim?</p>
            </div>
          }
          okButtonText="Sim, prosseguir"
          cancelButtonText="Cancelar"
          onConfirm={proceedFromModal}
          cancelButton={() => setIsConfirmModalOpen(false)}
        />
      )}
    </div>
  )
}
